/**
 * 🏆 BLUEPRINT VERSION & USAGE SERVICE
 *
 * Serviço dedicado a:
 *  - CRUD de process_blueprint_versions (snapshots imutáveis)
 *  - CRUD de process_blueprint_usage (rastreio de uso em projetos)
 *
 * Complementa excelenciaService.ts com persistência real
 * de versionamento e rastreabilidade.
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../../../lib/supabaseClient';
import { logGovernanceAction } from './blueprintLogService';
import {
  BLUEPRINTS_TABLE,
  BLUEPRINT_WITH_LATEST_VERSION_SELECT,
  mapBlueprintRow,
  scoreVersion,
  extractLatestVersion,
} from './blueprintDataHelpers';
import type {
  ProcessBlueprintVersion,
  ProcessBlueprintUsage,
  LinkBlueprintToProjectPayload,
  BlueprintUsageCount,
} from '../../../types/processBlueprint';

// ============================================================================
// HELPERS — Conversão snake_case ↔ camelCase
// ============================================================================

function mapRowToVersion(row: Record<string, any>): ProcessBlueprintVersion {
  return {
    id: row.id,
    blueprintId: row.blueprint_id,
    organizationId: row.organization_id,
    version: row.version,
    versionSemver: row.version_semver ?? '1.0.0',
    versionMajor: row.version_major ?? 1,
    versionMinor: row.version_minor ?? 0,
    versionPatch: row.version_patch ?? 0,
    changeType: row.change_type ?? 'patch',
    snapshotJson: row.snapshot ?? row.snapshot_json ?? {},
    snapshotHash: row.snapshot_hash ?? undefined,
    changeSummary: row.change_summary ?? undefined,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
  };
}

function mapRowToUsage(row: Record<string, any>): ProcessBlueprintUsage {
  return {
    id: row.id,
    blueprintId: row.blueprint_id,
    projectId: row.project_id,
    organizationId: row.organization_id,
    linkedBy: row.linked_by ?? undefined,
    linkedAt: row.linked_at,
    notes: row.notes ?? undefined,
    blueprintVersion: row.blueprint_version ?? 1,
    blueprintSemver: row.blueprint_semver ?? '1.0.0',
    statusAtLink: row.status_at_link ?? 'draft',
    categoryAtLink: row.category_at_link ?? 'geral',
    snapshotJson: row.snapshot ?? row.snapshot_json ?? {},
  };
}

// ============================================================================
// VERSIONS — CRUD
// ============================================================================

/**
 * Lista todas as versões de um blueprint específico.
 * Ordenadas por version DESC (mais recente primeiro).
 */
export async function listVersions(
  blueprintId: string,
): Promise<ProcessBlueprintVersion[]> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_versions')
    .select('*')
    .eq('blueprint_id', blueprintId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapRowToVersion);
}

/**
 * Busca uma versão específica por ID.
 */
export async function getVersionById(
  versionId: string,
): Promise<ProcessBlueprintVersion | null> {
  if (!versionId) throw new Error('versionId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_versions')
    .select('*')
    .eq('id', versionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data ? mapRowToVersion(data) : null;
}

/**
 * Exclui uma versão por ID.
 */
export async function deleteVersion(versionId: string): Promise<void> {
  if (!versionId) throw new Error('versionId é obrigatório');

  const { error } = await supabase
    .from('process_blueprint_versions')
    .delete()
    .eq('id', versionId);

  if (error) throw error;
}

// ============================================================================
// USAGE — CRUD
// ============================================================================

/**
 * Lista todos os usos de um blueprint específico (em quais projetos está vinculado).
 */
export async function listUsageByBlueprint(
  blueprintId: string,
): Promise<ProcessBlueprintUsage[]> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_usage')
    .select('*')
    .eq('blueprint_id', blueprintId);

  if (error) throw error;
  return (data || []).map(mapRowToUsage);
}

/**
 * Lista todos os blueprints vinculados a um projeto específico.
 */
export async function listUsageByProject(
  projectId: string,
): Promise<ProcessBlueprintUsage[]> {
  if (!projectId) throw new Error('projectId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_usage')
    .select('*')
    .eq('project_id', projectId)
    .order('linked_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapRowToUsage);
}

/**
 * Lista todos os usos na organização.
 */
export async function listAllUsage(
  organizationId: string,
): Promise<ProcessBlueprintUsage[]> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  const { data, error } = await supabase
    .from('process_blueprint_usage')
    .select('*')
    .eq('organization_id', organizationId);

  if (error) throw error;
  return (data || []).map(mapRowToUsage);
}

/**
 * Vincula um blueprint a um projeto.
 * Impede vinculação de blueprints arquivados.
 */
export async function linkBlueprintToProject(
  payload: LinkBlueprintToProjectPayload,
): Promise<ProcessBlueprintUsage> {
  if (!payload.blueprintId) throw new Error('blueprintId é obrigatório');
  if (!payload.projectId) throw new Error('projectId é obrigatório');
  if (!payload.organizationId) throw new Error('organizationId é obrigatório');

  // Validate: blueprint must belong to organization and not be archived
  const { data: bpRow, error: bpError } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select(BLUEPRINT_WITH_LATEST_VERSION_SELECT)
    .eq('id', payload.blueprintId)
    .eq('organization_id', payload.organizationId)
    .maybeSingle();

  if (bpError || !bpRow) throw new Error('Blueprint não encontrado');

  const blueprint = mapBlueprintRow(bpRow);
  if (blueprint.status === 'archived') {
    throw new Error('Blueprints arquivados não podem ser vinculados a projetos. Reative o blueprint primeiro.');
  }

  const versionNumeric = scoreVersion({
    version_major: blueprint.versionMajor,
    version_minor: blueprint.versionMinor,
    version_patch: blueprint.versionPatch,
  });

  const { data, error } = await supabase
    .from('process_blueprint_usage')
    .insert({
      blueprint_id: payload.blueprintId,
      organization_id: payload.organizationId,
    })
    .select('*')
    .single();

  if (error) throw error;
  const usage = mapRowToUsage(data);

  // Auditoria de uso
  logGovernanceAction({
    blueprintId: payload.blueprintId,
    organizationId: payload.organizationId,
    action: 'usage_linked',
    performedBy: payload.linkedBy,
    details: {
      projectId: payload.projectId,
      version: usage.blueprintVersion,
      versionSemver: usage.blueprintSemver,
      statusAtLink: usage.statusAtLink,
    },
  });

  return usage;
}

/**
 * Remove o vínculo de um blueprint com um projeto.
 */
export async function unlinkBlueprintFromProject(
  blueprintId: string,
  projectId: string,
): Promise<void> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');
  if (!projectId) throw new Error('projectId é obrigatório');

  const { data: usageRow } = await supabase
    .from('process_blueprint_usage')
    .select('organization_id')
    .eq('blueprint_id', blueprintId)
    .eq('project_id', projectId)
    .maybeSingle();

  const { error } = await supabase
    .from('process_blueprint_usage')
    .delete()
    .eq('blueprint_id', blueprintId)
    .eq('project_id', projectId);

  if (error) throw error;

  // Log removal (fire-and-forget)
  if (usageRow?.organization_id) {
    logGovernanceAction({
      blueprintId,
      organizationId: usageRow.organization_id,
      action: 'usage_unlinked',
      details: { projectId },
    }).catch(() => undefined);
  }
}

/**
 * Remove um uso por ID.
 */
export async function deleteUsage(usageId: string): Promise<void> {
  if (!usageId) throw new Error('usageId é obrigatório');

  const { error } = await supabase
    .from('process_blueprint_usage')
    .delete()
    .eq('id', usageId);

  if (error) throw error;
}

// ============================================================================
// AGGREGATED DATA — VIEW
// ============================================================================

/**
 * Retorna contagem de uso e versões por blueprint (da view).
 */
export async function getUsageCounts(
  organizationId: string,
): Promise<BlueprintUsageCount[]> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  const { data: blueprintRows, error: blueprintError } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select(BLUEPRINT_WITH_LATEST_VERSION_SELECT)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (blueprintError) throw blueprintError;

  const blueprints = (blueprintRows ?? []).map((row) => ({
    data: mapBlueprintRow(row),
    latestVersion: extractLatestVersion(row),
  }));
  const blueprintIds = blueprints.map((bp) => bp.data.id);
  if (blueprintIds.length === 0) return [];

  const { data: versionRows, error: versionError } = await supabase
    .from('process_blueprint_versions')
    .select('blueprint_id')
    .eq('organization_id', organizationId);
  if (versionError) {
    console.warn('[getUsageCounts] process_blueprint_versions query failed:', versionError.message);
  }
  const versionCountMap = new Map<string, number>();
  (versionRows ?? []).forEach((row) => {
    const id = row.blueprint_id as string | null;
    if (!id) return;
    versionCountMap.set(id, (versionCountMap.get(id) ?? 0) + 1);
  });

  const { data: usageRows, error: usageError } = await supabase
    .from('process_blueprint_usage')
    .select('blueprint_id')
    .eq('organization_id', organizationId);
  if (usageError) {
    console.warn('[getUsageCounts] process_blueprint_usage query failed:', usageError.message);
  }

  const usageCountMap = new Map<string, number>();

  (usageRows ?? []).forEach((row) => {
    const id = row.blueprint_id as string | null;
    if (!id) return;
    usageCountMap.set(id, (usageCountMap.get(id) ?? 0) + 1);
  });

  return blueprints.map(({ data, latestVersion }) => ({
    blueprintId: data.id,
    organizationId: data.organizationId,
    name: data.name,
    status: data.status,
    category: data.category,
    version: latestVersion?.version ?? 1,
    versionSemver: data.versionSemver,
    isCorporateStandard: data.isCorporateStandard,
    usageCount: usageCountMap.get(data.id) ?? 0,
    outdatedUsage: 0,
    projectsUsing: 0,
    versionCount: versionCountMap.get(data.id) ?? 0,
  }));
}
